import React, { useState } from 'react';
import { TrendingUp, Copy, Trash2, Calculator } from 'lucide-react';

const RoiCalculatorTool = () => {
  const [mode, setMode] = useState('basic');
  const [initialInvestment, setInitialInvestment] = useState('');
  const [finalValue, setFinalValue] = useState('');
  const [annualReturn, setAnnualReturn] = useState('');
  const [years, setYears] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    if (mode === 'basic') {
      const initial = parseFloat(initialInvestment);
      const final = parseFloat(finalValue);

      if (isNaN(initial) || isNaN(final) || initial === 0) {
        setResult(null);
        return;
      }

      const gain = final - initial;
      const roi = (gain / initial) * 100;

      setResult({
        mainValue: roi,
        mainLabel: 'Return on Investment (ROI)',
        isPercent: true,
        breakdown: [
          { label: 'Initial Investment', value: `$${initial.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Final Value', value: `$${final.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Net Gain/Loss', value: `$${gain.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, isPositive: gain >= 0 },
          { label: 'ROI', value: `${roi >= 0 ? '+' : ''}${roi.toFixed(2)}%`, isPositive: roi >= 0 }
        ]
      });
    } else if (mode === 'annualized') {
      const initial = parseFloat(initialInvestment);
      const final = parseFloat(finalValue);
      const y = parseFloat(years);

      if (isNaN(initial) || isNaN(final) || isNaN(y) || initial === 0 || y === 0) {
        setResult(null);
        return;
      }

      const totalROI = ((final - initial) / initial) * 100;
      // Annualized ROI = ((Final/Initial)^(1/years) - 1) × 100
      const annualizedROI = (Math.pow(final / initial, 1 / y) - 1) * 100;

      setResult({
        mainValue: annualizedROI,
        mainLabel: 'Annualized ROI',
        isPercent: true,
        breakdown: [
          { label: 'Initial Investment', value: `$${initial.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Final Value', value: `$${final.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Time Period', value: `${y} years` },
          { label: 'Total ROI', value: `${totalROI >= 0 ? '+' : ''}${totalROI.toFixed(2)}%`, isPositive: totalROI >= 0 },
          { label: 'Annualized ROI', value: `${annualizedROI >= 0 ? '+' : ''}${annualizedROI.toFixed(2)}%`, isPositive: annualizedROI >= 0 }
        ]
      });
    } else if (mode === 'future') {
      const initial = parseFloat(initialInvestment);
      const rate = parseFloat(annualReturn) / 100;
      const y = parseFloat(years);
      const monthly = parseFloat(monthlyContribution) || 0;

      if (isNaN(initial) || isNaN(rate) || isNaN(y)) {
        setResult(null);
        return;
      }

      // Future Value with compound interest
      let futureValue = initial * Math.pow(1 + rate, y);
      
      // Add future value of monthly contributions
      if (monthly > 0) {
        // FV of annuity: P × (((1 + r)^n - 1) / r) where r is monthly rate, n is months
        const monthlyRate = rate / 12;
        const months = y * 12;
        const contributionFV = monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
        futureValue += contributionFV;
      }

      const totalContributed = initial + (monthly * y * 12);
      const totalGain = futureValue - totalContributed;

      setResult({
        mainValue: futureValue,
        mainLabel: 'Future Value',
        isPrice: true,
        breakdown: [
          { label: 'Initial Investment', value: `$${initial.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Annual Return', value: `${(rate * 100).toFixed(2)}%` },
          { label: 'Time Period', value: `${y} years` },
          ...(monthly > 0 ? [{ label: 'Monthly Contribution', value: `$${monthly.toLocaleString(undefined, { minimumFractionDigits: 2 })}` }] : []),
          { label: 'Total Contributed', value: `$${totalContributed.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          { label: 'Total Gain', value: `$${totalGain.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, isPositive: totalGain >= 0 },
          { label: 'Future Value', value: `$${futureValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}` }
        ]
      });
    }
  };

  const copyResult = () => {
    if (result) {
      navigator.clipboard.writeText(result.mainValue.toFixed(2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = () => {
    setInitialInvestment('');
    setFinalValue('');
    setAnnualReturn('');
    setYears('');
    setMonthlyContribution('');
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-2">
        {[
          { key: 'basic', label: 'Basic ROI' },
          { key: 'annualized', label: 'Annualized ROI' },
          { key: 'future', label: 'Future Value' }
        ].map((m) => (
          <button
            key={m.key}
            onClick={() => { setMode(m.key); setResult(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === m.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        {mode === 'basic' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Initial Investment ($)
              </label>
              <input
                type="number"
                value={initialInvestment}
                onChange={(e) => setInitialInvestment(e.target.value)}
                placeholder="e.g., 10000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Final Value ($)
              </label>
              <input
                type="number"
                value={finalValue}
                onChange={(e) => setFinalValue(e.target.value)}
                placeholder="e.g., 15000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {mode === 'annualized' && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Initial Investment ($)
                </label>
                <input
                  type="number"
                  value={initialInvestment}
                  onChange={(e) => setInitialInvestment(e.target.value)}
                  placeholder="e.g., 10000"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Final Value ($)
                </label>
                <input
                  type="number"
                  value={finalValue}
                  onChange={(e) => setFinalValue(e.target.value)}
                  placeholder="e.g., 15000"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Time Period (Years)
              </label>
              <input
                type="number"
                value={years}
                onChange={(e) => setYears(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
          </>
        )}

        {mode === 'future' && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Initial Investment ($)
                </label>
                <input
                  type="number"
                  value={initialInvestment}
                  onChange={(e) => setInitialInvestment(e.target.value)}
                  placeholder="e.g., 10000"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Expected Annual Return (%)
                </label>
                <input
                  type="number"
                  value={annualReturn}
                  onChange={(e) => setAnnualReturn(e.target.value)}
                  placeholder="e.g., 8"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Time Period (Years)
                </label>
                <input
                  type="number"
                  value={years}
                  onChange={(e) => setYears(e.target.value)}
                  placeholder="e.g., 10"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Monthly Contribution (optional)
                </label>
                <input
                  type="number"
                  value={monthlyContribution}
                  onChange={(e) => setMonthlyContribution(e.target.value)}
                  placeholder="e.g., 500"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
                />
              </div>
            </div>
          </>
        )}
      </div>

      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
      >
        <Calculator size={18} />
        Calculate ROI
      </button>

      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-400 mb-1">{result.mainLabel}</div>
              <div className={`text-3xl font-bold ${result.mainValue >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.isPrice ? '$' : ''}{result.mainValue >= 0 && result.isPercent ? '+' : ''}{result.mainValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{result.isPercent ? '%' : ''}
              </div>
            </div>
            <button
              onClick={copyResult}
              className={`p-2 rounded transition-colors ${
                copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <Copy size={18} />
            </button>
          </div>

          <div className="border-t border-gray-700 pt-4">
            {result.breakdown.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm py-1">
                <span className="text-gray-400">{item.label}</span>
                <span className={`font-medium ${item.isPositive !== undefined ? (item.isPositive ? 'text-green-400' : 'text-red-400') : 'text-white'}`}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">ROI Benchmarks</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">S&P 500 (Historical)</div>
            <div>~10% annually</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Real Estate</div>
            <div>8-12% annually</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Bonds</div>
            <div>2-5% annually</div>
          </div>
        </div>
      </div>

      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default RoiCalculatorTool;
